import ProjectsState, { CurrentFolderId } from '@states/Projects';
import { MemberState } from '@states/Viewer';
import { addColumns, getFolderColumns } from '@utils/columnUtils.js';

// Template column definitions (matches backend projectTemplates.js)
const TEMPLATE_COLUMNS = {
  projects: [
    { name: 'Name', type: 'text' },
    { name: 'Description', type: 'text' },
    { name: 'Status', type: 'select' },
    { name: 'Assigned People', type: 'people' },
    { name: 'Assigned Departments', type: 'select' },
    { name: 'Deadline', type: 'date' },
  ],
  crm: [
    { name: 'Name', type: 'text' },
    { name: 'First Name', type: 'text' },
    { name: 'Last Name', type: 'text' },
    { name: 'Work Email', type: 'text' },
    { name: 'Work Phone', type: 'text' },
    { name: 'Organization', type: 'text' },
    { name: 'Role', type: 'text' },
  ],
  purchase: [
    { name: 'Name', type: 'text' },
    { name: 'Description', type: 'text' },
    { name: 'Status', type: 'select' },
    { name: 'Lead Time', type: 'number' },
    { name: 'Unit Price', type: 'number' },
    { name: 'QTY', type: 'number' },
    { name: 'Total', type: 'number' },
    { name: 'Link', type: 'link' },
    { name: 'Manufacturer', type: 'text' },
    { name: 'Distributor', type: 'text' },
  ],
  calendar: [
    { name: 'Name', type: 'text' },
    { name: 'Description', type: 'text' },
    { name: 'Start Time', type: 'date' },
    { name: 'End Time', type: 'date' },
    { name: 'Timezone', type: 'text' },
    { name: 'Location', type: 'text' },
    { name: 'Attendees', type: 'people' },
    { name: 'Link', type: 'link' },
    { name: 'Status', type: 'select' },
  ],
  email: [
    { name: 'Name', type: 'text' },
    { name: 'Email Context', type: 'text' },
    { name: 'Signature', type: 'text' },
    { name: 'Sent By', type: 'people' },
    { name: 'To', type: 'text' },
    { name: 'CC', type: 'text' },
    { name: 'BCC', type: 'text' },
    { name: 'Scheduled For', type: 'date' },
  ],
  repository: [
    { name: 'Name', type: 'text' },
    { name: 'Description', type: 'text' },
    { name: 'Created By', type: 'people' },
    { name: 'Department', type: 'select' },
    { name: 'Shared With', type: 'people' },
    { name: 'File Size', type: 'text' },
  ],
  inventory: [
    { name: 'Name', type: 'text' },
    { name: 'Description', type: 'text' },
    { name: 'Stock Location', type: 'text' },
    { name: 'QTY', type: 'number' },
    { name: 'Unit Price', type: 'number' },
    { name: 'Manufacturer', type: 'text' },
    { name: 'Link', type: 'link' },
    { name: 'Seller', type: 'text' },
  ],
};

/**
 * Download a CSV template for a specific template type
 * @param {string} templateId - The template ID (e.g., 'crm', 'inventory')
 */
export function downloadTemplateForType(templateId) {
  try {
    const columns = TEMPLATE_COLUMNS[templateId];
    if (!columns) {
      console.error(`Unknown template: ${templateId}`);
      return;
    }

    const headers = columns.map(col => col.name);
    const headerTypes = columns.map(col => col.type);

    const rows = [headers.join(','), headerTypes.join(',')];
    const csvString = rows.join('\r\n');

    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const templateName =
      templateId.charAt(0).toUpperCase() + templateId.slice(1);
    const file = new File([blob], `${templateName}_Template.csv`, {
      type: 'text/csv',
    });
    createCSVFileDownload(file);
  } catch (error) {
    console.error('Error downloading template:', error);
  }
}

/**
 * Get available template types for UI
 */
export function getTemplateTypes() {
  return [
    { id: 'projects', name: 'Projects', icon: 'folder' },
    { id: 'crm', name: 'CRM', icon: 'users' },
    { id: 'purchase', name: 'Purchase', icon: 'shopping-cart' },
    { id: 'calendar', name: 'Calendar', icon: 'calendar' },
    { id: 'email', name: 'Email', icon: 'mail' },
    { id: 'repository', name: 'Repository', icon: 'file' },
    { id: 'inventory', name: 'Inventory', icon: 'package' },
  ];
}

export async function onDownloadTemplate(currentFolderId) {
  try {
    // Get current column headers based on the current folder context
    const columns = getFolderColumns(currentFolderId);

    // Create headers array: Name + all column titles
    const headers = [...columns.map(col => col.title)];
    const headerTypes = [...columns.map(col => col.type)];

    // remove the Created At column that is default and created in the BE - maybe should indicate it to the users
    const createdAtIndex = headers.indexOf('Created At');
    if (createdAtIndex !== -1) {
      headers.splice(createdAtIndex, 1);
      headerTypes.splice(createdAtIndex, 1);
    }

    const rows = [headers.join(','), headerTypes.join(',')];

    // Create CSV content with headers only
    const csvString = rows.join('\r\n');

    // Create and download the CSV file
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const file = new File([blob], 'Template.csv', { type: 'text/csv' });
    createCSVFileDownload(file);
  } catch (error) {
    console.error('Error downloading template:', error);
  }
}

const getSelectOptionKey = (columnId, selectedValue) => {
  const column = getFolderColumns(CurrentFolderId.currentFolderId).find(
    col => col.id === columnId,
  );
  if (!column || !column.options) return null;

  // options is an object: key -> optionObj, match by label
  for (const [key, opt] of Object.entries(column.options)) {
    if (opt.label === selectedValue) {
      return key;
    }
  }
  return null;
};

// Use the same logic as the original CSV generation
const getColumnValue = (item, col) => {
  let value;

  const columnValue = item.columnValues.find(
    cv => cv.columnId === col.id || cv.columnId === col.key,
  );
  value = columnValue ? columnValue.value : item[col.key] || '';

  // Special handling for assignees column
  if (col.type === 'people' && value) {
    // Get organization users for 'people' column
    const orgUsers = MemberState.members;

    try {
      const assigneeIds = JSON.parse(value);
      if (Array.isArray(assigneeIds)) {
        const assigneeEmails = assigneeIds.map(id => {
          const user = orgUsers.find(u => u.uid === id);
          return user ? user.email : id;
        });
        value = assigneeEmails.join('; ');
      }
    } catch (e) {
      console.error('[CSV DEBUG] Error parsing assignees:', e);
    }
  }

  // Special handling for status and select columns
  if (col.type === 'select' && value && col.options) {
    const option = col.options[value];
    if (option && option.label) {
      value = option.label;
    }
  }

  return value;
};

export async function convertAllItemsToCSV(allItems, currentFolderId) {
  try {
    const columns = getFolderColumns(currentFolderId);

    // Create headers from columns
    const headers = [...columns.map(col => col.title)];
    // Make a double header with the type
    const headerTypes = [...columns.map(col => col.type)];
    const rows = [headers.join(','), headerTypes.join(',')];

    allItems.forEach(item => {
      const values = [];
      columns.forEach(col => {
        values.push(getColumnValue(item, col));
      });
      const csvRow = values
        .map(v => `"${(v || '').replace(/"/g, '""')}"`)
        .join(',');
      rows.push(csvRow);
    });

    const csvString = rows.join('\r\n');

    // Get project name for the filename
    const projectName = currentFolderId
      ? ProjectsState.projects.folders
          ?.find(f => f.id === currentFolderId)
          .name.replace(/[^a-zA-Z0-9]/g, '-')
      : 'Project';

    const fileName = `${projectName}-list.csv`;

    // Create Blob and File
    const blob = new Blob([csvString], { type: 'text/csv' });
    const file = new File([blob], fileName, { type: 'text/csv' });

    return { file };
  } catch (error) {
    console.error('Error converting all items to CSV:', error);
    throw new Error(
      i18next.t('pages.projects.projectDataActions.failedToParseCSV'),
    );
  }
}

function parseCSVContent(text) {
  const rows = [];
  const lines = text.split(/\r?\n/);
  let currentRow = [];
  let currentField = '';
  let inQuotes = false;
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex];
    for (let charIndex = 0; charIndex < line.length; charIndex++) {
      const char = line[charIndex];
      if (char === '"') {
        if (inQuotes && line[charIndex + 1] === '"') {
          currentField += '"';
          charIndex++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        currentRow.push(currentField.trim());
        currentField = '';
      } else {
        currentField += char;
      }
    }
    if (!inQuotes) {
      currentRow.push(currentField.trim());
      currentField = '';
      if (currentRow.length > 0) {
        rows.push(currentRow);
      }
      currentRow = [];
    } else {
      currentField += '\n';
    }
  }
  if (currentField.trim()) {
    currentRow.push(currentField.trim());
  }
  if (currentRow.length > 0) {
    rows.push(currentRow);
  }
  return rows;
}

// this function extract the csv data and prepare the data for the APIs
export async function onUploadCsv(file, currentFolderId) {
  const text = await file.text();
  // parse CSV content robustly
  const rows = parseCSVContent(text);
  if (rows.length < 2) {
    throw new Error(
      i18next.t('pages.projects.projectDataActions.csvMustHaveTwoHeaderRows'),
    );
  }
  const [headers, types, ...dataRows] = rows;
  // check if headers and types are valid
  if (headers.length === 0 || types.length === 0) {
    throw new Error(
      i18next.t(
        'pages.projects.projectDataActions.csvMustContainHeadersAndTypes',
      ),
    );
  }
  // check if type are valid (e.g., text, select, date, people, number, checkbox)
  const validTypes = [
    'text',
    'select',
    'date',
    'people',
    'number',
    'checkbox',
    'link',
  ];
  for (const type of types) {
    if (!validTypes.includes(type)) {
      throw new Error(
        i18next.t('pages.projects.projectDataActions.invalidColumnType', {
          type,
          validTypes: validTypes.join(', '),
        }),
      );
    }
    // check if we are on personal or team project -> people type is team based
    const isNotTeamProject =
      ProjectsState.projects.rootFolderOrganizationId === null;
    if (isNotTeamProject) {
      // If it's not a team project, ensure no "people" type columns are present
      if (types.includes('people')) {
        throw new Error(
          i18next.t(
            'pages.projects.projectDataActions.personalProjectsCannotHavePeopleColumns',
          ),
        );
      }
    }
  }

  // Validate that 'Name' column exists and has correct type
  const nameColumnIndex = headers.findIndex(
    header => header.toLowerCase() === 'name',
  );
  if (nameColumnIndex === -1) {
    throw new Error(
      i18next.t('pages.projects.projectDataActions.csvMustContainNameColumn'),
    );
  }

  const nameColumnType = types[nameColumnIndex];
  if (nameColumnType !== 'text') {
    throw new Error(
      i18next.t('pages.projects.projectDataActions.nameColumnMustBeText'),
    );
  }

  const listColumns = getFolderColumns(currentFolderId);
  const headersNameTypes = [];
  const missingColumns = [];
  const duplicateNameColumnsDiffType = [];

  headers.forEach((header, index) => {
    const col = listColumns.find(
      c =>
        c.title.toLowerCase() === header.toLowerCase() &&
        c.type === types[index],
    );

    // Check for columns with same name but different type
    const sameNameColumn = listColumns.find(
      c => c.title.toLowerCase() === header.toLowerCase(),
    );

    if (col) {
      // Column exists with same name and type
      headersNameTypes.push({ title: header, type: types[index], id: col.id });
    } else if (sameNameColumn) {
      // Column exists with same name but different type
      duplicateNameColumnsDiffType.push({
        title: header,
        type: types[index],
        existingType: sameNameColumn.type,
      });
      headersNameTypes.push({
        title: header,
        type: types[index],
        id: null,
        missing: true,
        duplicate: true,
      });
    } else {
      // Column is missing
      headersNameTypes.push({
        title: header,
        type: types[index],
        id: null,
        missing: true,
      });
      missingColumns.push({ title: header, type: types[index] });
    }
  });

  // Check for duplicate column names within the CSV itself
  const csvColumnNames = headers.map(h => h.toLowerCase());
  const csvDuplicates = csvColumnNames.filter(
    (name, index) => csvColumnNames.indexOf(name) !== index,
  );

  if (csvDuplicates.length > 0) {
    throw new Error(
      `Duplicate column names found in CSV: ${[...new Set(csvDuplicates)].join(', ')}`,
    );
  }

  // If there are columns with duplicate names but different types, throw error
  if (duplicateNameColumnsDiffType.length > 0) {
    const duplicateMessages = duplicateNameColumnsDiffType.map(
      col =>
        `"${col.title}" (CSV type: ${col.type}, existing type: ${col.existingType})`,
    );
    throw new Error(
      `Column name conflicts detected. The following columns already exist with different types:\n${duplicateMessages.join('\n')}\n\nPlease rename these columns in your CSV or change their types to match existing columns.`,
    );
  }

  // Create the new rows projects or tasks
  const extractedData = dataRows.map(row => {
    // Find name data related to the column (unique and fixed)
    const nameColumn = headersNameTypes.find(
      header => header.title.toLowerCase() === 'name',
    );
    return row[headers.indexOf(nameColumn.title)];
  });

  // Validate the Name column: check for empty values and duplicates
  const nameValidationErrors = [];
  const nameSet = new Set();
  const duplicateNames = new Set();

  extractedData.forEach((name, index) => {
    // Check for empty/null names
    if (!name || name.trim() === '') {
      nameValidationErrors.push(`Row ${index + 3}: Name cannot be empty`); // +3 because of headers and 1-based indexing
    } else {
      const trimmedName = name.trim();
      // Check for duplicates only for projects because tasks can have non-unique names
      if (nameSet.has(trimmedName.toLowerCase()) && currentFolderId === null) {
        duplicateNames.add(trimmedName);
      }
      nameSet.add(trimmedName.toLowerCase());
    }
  });

  // Add duplicate name errors
  if (duplicateNames.size > 0) {
    const duplicateList = Array.from(duplicateNames).join(', ');
    nameValidationErrors.push(`Duplicate names found: ${duplicateList}`);
  }

  // Throw error if validation fails
  if (nameValidationErrors.length > 0) {
    throw new Error(
      i18next.t(
        'pages.projects.projectDataActions.nameColumnValidationFailed',
        {
          errors: nameValidationErrors.join('\n'),
        },
      ),
    );
  }

  // Additional validation for root folder: check against existing project names
  if (currentFolderId === null) {
    const existingProjects = ProjectsState.projects.folders || [];
    const existingProjectNames = existingProjects.map(project =>
      project.name.toLowerCase().trim(),
    );

    const conflictingNames = [];
    extractedData.forEach(name => {
      const trimmedName = name.trim().toLowerCase();
      if (existingProjectNames.includes(trimmedName)) {
        conflictingNames.push(name.trim());
      }
    });

    if (conflictingNames.length > 0) {
      throw new Error(
        `Project name conflicts detected. The following project names already exist:\n${conflictingNames.join(', ')}\n\nPlease rename these projects in your CSV as project names must be unique.`,
      );
    }
  }

  const newColumnValues = dataRows.map(row => {
    const columnValues = {};
    headersNameTypes.forEach(header => {
      // no need to process name column as it is created during the project creation
      if (header.title.toLowerCase() === 'name') {
        return;
      }

      // Skip missing columns (they will be handled separately)
      if (header.missing || !header.id) {
        return;
      }

      if (header.type === 'select') {
        // if select with need to get the key of the corresponding option
        const selectedOption = row[headers.indexOf(header.title)];
        const optionKey = getSelectOptionKey(header.id, selectedOption);
        columnValues[header.title] = {
          id: header.id,
          text: selectedOption,
          value: optionKey,
          type: header.type,
        };
        return;
      }

      if (header.type === 'people') {
        const orgUsers = MemberState.members;
        // we have to get the ids of the people based on their emails
        const emails = row[headers.indexOf(header.title)]
          ?.split(',')
          .map(email => email.trim());
        const userIds = orgUsers
          .filter(user => emails?.includes(user.email))
          .map(user => user.uid);
        columnValues[header.title] = {
          id: header.id,
          value: JSON.stringify(userIds),
          type: header.type,
        };
        return;
      }
      columnValues[header.title] = {
        id: header.id,
        value: row[headers.indexOf(header.title)],
        type: header.type,
      };
    });
    return columnValues;
  });

  return {
    extractedData,
    newColumnValues,
    headers,
    types,
    missingColumns,
    headersNameTypes,
    csvData: { headers, dataRows }, // Add raw CSV data for column creation
  };
}

// Helper function to generate random colors
const generateRandomColor = () => {
  const colors = [
    '#e3f2fd',
    '#f3e5f5',
    '#e8f5e8',
    '#fff3e0',
    '#fce4ec',
    '#e0f2f1',
    '#f1f8e9',
    '#e8eaf6',
    '#fff8e1',
    '#ffebee',
    '#e3f0ff',
    '#f0f4c3',
    '#ede7f6',
    '#e0f7fa',
    '#ffecb3',
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

// Helper function to extract unique values for a select column
const extractUniqueValues = (columnTitle, headers, dataRows) => {
  const columnIndex = headers.indexOf(columnTitle);
  if (columnIndex === -1) return [];

  const uniqueValues = new Set();
  dataRows.forEach(row => {
    const value = row[columnIndex];
    if (
      value &&
      value.trim() !== '' &&
      value.trim().toLowerCase() !== 'null' &&
      value.trim().toLowerCase() !== 'undefined'
    ) {
      uniqueValues.add(value.trim());
    }
  });

  return Array.from(uniqueValues).sort(); // Sort alphabetically for consistency
};

// Create missing columns in bulk and update the headersNameTypes with the new column IDs
export async function createMissingColumns(
  missingColumns,
  currentFolderId,
  csvData = null,
) {
  if (!missingColumns || missingColumns.length === 0) {
    return [];
  }

  // Prepare bulk column data
  const columnsToCreate = [];

  // Get current columns to calculate order
  const existingColumns = getFolderColumns(currentFolderId);
  let maxOrder = Math.max(0, ...existingColumns.map(c => c.order || 0));

  for (const missingColumn of missingColumns) {
    const columnData = {
      name: missingColumn.title,
      type: missingColumn.type,
      organizationId: ProjectsState.projects.rootFolderOrganizationId,
      projectId: currentFolderId, // Use currentFolderId as projectId, null for root
      order: ++maxOrder, // Increment for each new column
      width: 150,
      hidden: false,
      isCustom: true,
    };

    // Create options dynamically for select columns based on CSV data
    if (missingColumn.type === 'select' && csvData) {
      const { headers, dataRows } = csvData;
      const uniqueValues = extractUniqueValues(
        missingColumn.title,
        headers,
        dataRows,
      );

      if (uniqueValues.length > 0) {
        const options = {};
        const usedKeys = new Set();

        uniqueValues.forEach((value, index) => {
          // Create a meaningful key based on the value
          const sanitizedValue = value
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '_')
            .replace(/_{2,}/g, '_')
            .replace(/^_|_$/g, '');

          // Handle empty sanitized values or ensure uniqueness
          let optionKey = sanitizedValue || `option_${index + 1}`;
          let counter = 1;
          while (usedKeys.has(optionKey)) {
            optionKey = `${sanitizedValue || 'option'}_${counter}`;
            counter++;
          }
          usedKeys.add(optionKey);

          options[optionKey] = {
            label: value,
            color: generateRandomColor(),
          };
        });
        columnData.options = options;
      } else {
        // Fallback to default options if no values found
        columnData.options = {
          option1: { label: 'Option 1', color: generateRandomColor() },
        };
      }
    }

    columnsToCreate.push(columnData);
  }

  try {
    // Create all columns in a single bulk request using columnUtils
    const newColumns = await addColumns(columnsToCreate, currentFolderId);

    // Map the response back to the original missing columns format
    const createdColumns = newColumns.map((newColumn, index) => ({
      ...missingColumns[index],
      id: newColumn.id,
      created: true,
    }));

    return createdColumns;
  } catch (error) {
    console.error('Failed to create columns in bulk:', error);
    throw error;
  }
}

/* UTILS */
export function createCSVFileDownload(file) {
  const downloadLink = document.createElement('a');
  downloadLink.href = URL.createObjectURL(file);
  downloadLink.download = file.name;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
  URL.revokeObjectURL(downloadLink.href);
}
